import React, { useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, CloudUpload, Delete, Download, Edit, Search, SyncAlt } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { getAuthHeaders, makeApiCall } from '../../utils/apiConfig';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useToast } from '../../contexts/ToastContext';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';
import { LockedFeature } from '../planLock/LockedFeature';
import { usePlanSurface } from '../planLock/usePlanSurface';
import { placeholderApi } from '../../services/placeholderApi';
import { tokens } from '../../theme/tokens';
import { UnmappedReviewCard } from './UnmappedReviewCard';
import type { MapManuallyRequest, UnmappedSource } from './UnmappedReviewCard';
import { CatalogQueueCard, QUEUE_STATE_TONE } from './CatalogQueueCard';
import { ConditionPricingCard } from './ConditionPricingCard';
import { StoreUnitsTab } from './StoreUnitsTab';
import type { QueueState } from './CatalogQueueCard';
import { useUrlTab } from '../../hooks/useUrlTab';
import { ProductsCsvExportButton } from '../ProductsCsvExportButton';
import { templateColumns } from '../../utils/csvTemplate';
import { SKU_CROSSWALK_TEMPLATE } from '../../constants/csvTemplates';
import {
  BrandSelect,
  ConfirmDialog,
  EmptyState,
  FormDialog,
  InlineMessage,
  LoadingState,
  PageContainer,
  PageHeader,
  ResponsiveRows,
  SectionCard,
  StatusChip,
  CsvTemplateHint,
  TabShell,
} from '../ui';
import type { StatusTone } from '../ui';

// Columns the server reads: `storeSkuMappingService.parseCsv` and its `HEADER_ALIASES`
// (`src/services/storeSkuMappingService.ts`). A row needs one of upc / catalog_sku / product_id to name the
// product and one of store_sku / barcode to write; `condition` must be one of `STORE_SKU_CONDITIONS`.
// `product_id` is listed last and described as the export-only column: a store new to Motzu has no
// product ids and names the product by UPC or manufacturer SKU, which the product browser shows.
const CROSSWALK_REQUIRED: Record<string, boolean | 'one-of'> = {
  upc: 'one-of',
  catalog_sku: 'one-of',
  product_id: 'one-of',
  store_sku: 'one-of',
  barcode: 'one-of',
};

export type CrosswalkCondition = 'new' | 'open_box' | 'demo' | 'display';
const CONDITIONS: CrosswalkCondition[] = ['new', 'open_box', 'demo', 'display'];
const CONDITION_TONE: Record<CrosswalkCondition, StatusTone> = {
  new: 'positive',
  open_box: 'caution',
  demo: 'info',
  display: 'neutral',
};

interface CrosswalkProduct {
  id: string;
  name: string;
  brand?: string;
  sku?: string;
  upc?: string;
}

/** What a row resolves to (flag `catalog_queue_window`); a row without one names a catalog product. */
export type CrosswalkResolutionKind = 'catalog' | 'placeholder' | 'staged' | 'unresolved';
const RESOLUTION_KINDS: CrosswalkResolutionKind[] = ['catalog', 'placeholder', 'staged', 'unresolved'];
const RESOLUTION_TONE: Record<CrosswalkResolutionKind, StatusTone> = {
  catalog: 'positive',
  placeholder: 'info',
  staged: 'caution',
  unresolved: 'neutral',
};
type PreviewResolution = 'ready' | 'placeholder' | 'queued' | 'unresolved' | 'ambiguous';
const PREVIEW_TONE: Record<PreviewResolution, StatusTone> = {
  ready: 'positive',
  placeholder: 'info',
  queued: 'caution',
  unresolved: 'neutral',
  ambiguous: 'problem',
};

interface CrosswalkMapping {
  id: string;
  /** Null while the row names a staged product or nothing yet. */
  productId: string | null;
  resolution?: { kind: CrosswalkResolutionKind; stagedProductId?: string };
  storeSku?: string;
  storeBarcode?: string;
  condition: CrosswalkCondition;
  integrationId?: string | null;
  label?: string;
  product?: CrosswalkProduct;
  /** `merge_approval`: written when the store approved a barcode match on the Shopify merge card. */
  source?: 'merge_approval';
}

interface LegacyOverride {
  id: string;
  upc: string;
  matchSku?: string;
  storeSku: string;
  storeBarcode?: string;
  productName?: string;
}

interface IntegrationOption {
  id: string;
  platform?: string;
  storeName?: string;
  name?: string;
}

interface CsvPreview {
  totalRows: number;
  errors: Array<{ row: number; error: string }>;
  /** Per preview row, what the import would do with it (flag `catalog_queue_window`). */
  resolutions?: Array<{ row: number; resolution: PreviewResolution; name?: string; message?: string }>;
  preview?: Array<{ rowNumber: number; storeSku?: string; storeBarcode?: string; upc?: string; catalogSku?: string; productId?: string }>;
  /** Rows that named a product but set no store SKU or barcode (a partly filled "my products" sheet). */
  blank?: number;
  /** Non-blocking notes, e.g. a UPC that lost its leading zero in a spreadsheet. */
  warnings?: Array<{ row: number; warning: string }>;
}

interface CsvResult {
  total: number;
  created: number;
  updated: number;
  skipped: number;
  resolution?: Record<CrosswalkResolutionKind, number>;
  blank?: number;
  errors: Array<{ row: number; error: string }>;
  warnings?: Array<{ row: number; warning: string }>;
}

interface FormState {
  id?: string;
  product: CrosswalkProduct | null;
  storeSku: string;
  storeBarcode: string;
  condition: CrosswalkCondition;
  integrationId: string;
  label: string;
}

const emptyForm = (): FormState => ({
  product: null,
  storeSku: '',
  storeBarcode: '',
  condition: 'new',
  integrationId: '',
  label: '',
});

const productLabel = (p: CrosswalkProduct): string =>
  [p.brand, p.name, p.sku ? `· ${p.sku}` : ''].filter(Boolean).join(' ');

const errorMessage = async (response: Response, fallback: string): Promise<string> => {
  const body = await response.json().catch(() => ({}));
  return body?.error || fallback;
};

const COLUMNS = [
  { key: 'product', header: 'product', width: 'minmax(0,1.6fr)' },
  { key: 'sku', header: 'sku', width: 'minmax(0,1fr)', mobileLabel: true },
  { key: 'barcode', header: 'barcode', width: 'minmax(0,1fr)', mobileLabel: true },
  { key: 'condition', header: 'condition', width: '120px', mobileLabel: true },
  { key: 'integration', header: 'integration', width: 'minmax(0,1fr)', mobileLabel: true },
  { key: 'actions', header: 'actions', width: '88px', align: 'end' as const },
];

/**
 * The store's SKU + barcode mapping (flag `sku_crosswalk`), rendered by
 * SkuOverridesPage in place of the one-row-per-UPC override table. Several
 * mappings per catalog product sit together, each with a condition and an
 * optional integration.
 */
export const SkuCrosswalkPanel: React.FC = () => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  // `?review=shopify|sold` opens the unmapped review on that list (dashboard and Sales Report links).
  const [searchParams] = useSearchParams();
  const reviewParam = searchParams.get('review');
  const reviewSource: UnmappedSource | null = reviewParam === 'sold' || reviewParam === 'shopify' ? reviewParam : null;

  // Flag `catalog_queue_window`: rows carry a resolution, the import preview names each row's
  // outcome, and a Catalog queue tab lists the store's queued products.
  const { enabled: queueWindowEnabled } = useOrgFeatureFlag('catalog_queue_window');
  // Flag `condition_pricing` (slice 2): a Condition pricing tab (one rule per condition) and a Units
  // tab (per-unit listings). Off, both are hidden and the page is exactly slice 1.
  const { enabled: conditionPricingEnabled } = useOrgFeatureFlag('condition_pricing');
  const conditionSurface = usePlanSurface({ feature: 'customPricing', orgFlag: 'condition_pricing' });
  const conditionTabsVisible = conditionPricingEnabled || conditionSurface.mode === 'locked';
  const [tab, setTab] = useUrlTab(['crosswalk', 'pricing', 'units', 'queue'] as const, 'crosswalk');
  const tabsShown = queueWindowEnabled || conditionTabsVisible;
  const activeTab =
    (tab === 'queue' && !queueWindowEnabled) || ((tab === 'pricing' || tab === 'units') && !conditionTabsVisible)
      ? 'crosswalk'
      : tab;

  const [search, setSearch] = useState('');
  const [conditionFilter, setConditionFilter] = useState<'' | CrosswalkCondition>('');
  const [resolutionFilter, setResolutionFilter] = useState<'' | CrosswalkResolutionKind>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [productQuery, setProductQuery] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [toDelete, setToDelete] = useState<CrosswalkMapping | null>(null);
  const [legacyToDelete, setLegacyToDelete] = useState<LegacyOverride | null>(null);

  // Pricing-only placeholders (flag `placeholder_link_only`): a SKU or barcode Motzu does not know.
  const { enabled: placeholdersEnabled } = useOrgFeatureFlag('placeholder_link_only');
  const [placeholderOpen, setPlaceholderOpen] = useState(false);
  const [placeholderBrand, setPlaceholderBrand] = useState('');
  const [placeholderTitle, setPlaceholderTitle] = useState('');

  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<CsvPreview | null>(null);
  const [importResult, setImportResult] = useState<CsvResult | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const listKey = ['skuCrosswalk', page, rowsPerPage, search, conditionFilter, resolutionFilter];
  const { data, isLoading, error } = useQuery({
    queryKey: listKey,
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(rowsPerPage),
        offset: String(page * rowsPerPage),
        ...(search ? { search } : {}),
        ...(conditionFilter ? { condition: conditionFilter } : {}),
        ...(resolutionFilter ? { resolution: resolutionFilter } : {}),
      });
      const response = await makeApiCall(`/store/sku-crosswalk?${params}`, { headers: await getAuthHeaders() });
      if (!response.ok) throw new Error(await errorMessage(response, t('store.skuCrosswalk.loadError')));
      return response.json() as Promise<{ mappings: CrosswalkMapping[]; total: number; resolutionCounts?: Record<CrosswalkResolutionKind, number> }>;
    },
  });
  // Rows and chips the queue tab needs for the "In queue · state" chip, joined by staged id.
  const { data: queueStates } = useQuery({
    queryKey: ['skuCrosswalkQueueStates'],
    enabled: queueWindowEnabled,
    queryFn: async () => {
      const response = await makeApiCall('/store/sku-crosswalk/queue?limit=500', { headers: await getAuthHeaders() });
      if (!response.ok) return new Map<string, QueueState>();
      const body = (await response.json()) as { items?: Array<{ stagedProductId: string; queueState: QueueState }> };
      return new Map((body.items || []).map((i) => [i.stagedProductId, i.queueState]));
    },
  });

  const { data: integrations } = useQuery<IntegrationOption[]>({
    queryKey: ['integrations'],
    queryFn: async () => {
      const response = await makeApiCall('/integrations', { headers: await getAuthHeaders() });
      if (!response.ok) return [];
      return (await response.json()).data || [];
    },
  });

  const { data: legacyData } = useQuery({
    queryKey: ['skuCrosswalkLegacy'],
    queryFn: async () => {
      const response = await makeApiCall('/store/sku-crosswalk/legacy', { headers: await getAuthHeaders() });
      if (!response.ok) return { legacy: [] as LegacyOverride[] };
      return response.json() as Promise<{ legacy: LegacyOverride[] }>;
    },
  });
  const legacyRows = legacyData?.legacy || [];

  const integrationName = (id?: string | null): string => {
    if (!id) return t('store.skuCrosswalk.allIntegrations');
    const match = (integrations || []).find((i) => i.id === id);
    return match ? match.storeName || match.name || match.platform || id : id;
  };

  // Debounce the catalog search so typing does not query per keystroke.
  React.useEffect(() => {
    const handle = window.setTimeout(() => setProductSearch(productQuery.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [productQuery]);

  const { data: productHits, isFetching: productsLoading } = useQuery({
    queryKey: ['skuCrosswalkProductSearch', productSearch],
    enabled: formOpen && productSearch.length >= 2,
    queryFn: async () => {
      const result = await apiService.getStoreProducts(20, 0, { search: productSearch });
      return result.data || [];
    },
  });

  const productOptions = useMemo<CrosswalkProduct[]>(() => {
    const hits = (productHits || []).map((p) => ({
      id: p.id,
      name: p.name || p.model || p.id,
      brand: p.brand,
      sku: p.sku,
    }));
    return form.product && !hits.some((h) => h.id === form.product!.id) ? [form.product, ...hits] : hits;
  }, [productHits, form.product]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['skuCrosswalk'] });
    queryClient.invalidateQueries({ queryKey: ['skuCrosswalkLegacy'] });
    queryClient.invalidateQueries({ queryKey: ['skuCrosswalkQueue'] });
    queryClient.invalidateQueries({ queryKey: ['skuCrosswalkQueueStates'] });
  };

  const saveMutation = useMutation({
    mutationFn: async (state: FormState) => {
      const response = await makeApiCall('/store/sku-crosswalk', {
        method: 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          ...(state.id ? { id: state.id } : {}),
          // An edit of a staged or unresolved row names no product and keeps the row's own link.
          ...(state.product?.id ? { productId: state.product.id } : {}),
          storeSku: state.storeSku.trim() || null,
          storeBarcode: state.storeBarcode.trim() || null,
          condition: state.condition,
          integrationId: state.integrationId || null,
          label: state.label.trim() || null,
        }),
      });
      if (!response.ok) throw new Error(await errorMessage(response, t('store.skuCrosswalk.saveError')));
      return response.json();
    },
    onSuccess: (_result, state) => {
      invalidate();
      showToast(t(state.id ? 'store.skuCrosswalk.updated' : 'store.skuCrosswalk.created'), 'success');
      setFormOpen(false);
      setForm(emptyForm());
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const placeholderMutation = useMutation({
    mutationFn: (state: FormState) =>
      placeholderApi.createFromListing({
        brand: placeholderBrand.trim(),
        title: placeholderTitle.trim(),
        sku: state.storeSku.trim() || null,
        barcode: state.storeBarcode.trim() || null,
      }),
    onSuccess: (result, state) => {
      const name = placeholderTitle.trim() || state.storeSku.trim() || state.storeBarcode.trim();
      setForm((f) => ({
        ...f,
        product: { id: result.productId, name, brand: placeholderBrand.trim(), sku: state.storeSku.trim() || undefined },
      }));
      setPlaceholderOpen(false);
      queryClient.invalidateQueries({ queryKey: ['store-placeholders'] });
      showToast(t('store.placeholders.crosswalkCreated'), 'success');
    },
    onError: (err: Error) => showToast(err.message || t('store.placeholders.crosswalkError'), 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (mapping: CrosswalkMapping) => {
      const response = await makeApiCall(`/store/sku-crosswalk/${encodeURIComponent(mapping.id)}`, {
        method: 'DELETE',
        headers: await getAuthHeaders(),
      });
      if (!response.ok) throw new Error(await errorMessage(response, t('store.skuCrosswalk.deleteError')));
    },
    onSuccess: () => {
      invalidate();
      showToast(t('store.skuCrosswalk.deleted'), 'success');
      setToDelete(null);
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const deleteLegacyMutation = useMutation({
    mutationFn: async (row: LegacyOverride) => {
      const response = await makeApiCall(`/store/sku-crosswalk/legacy/${encodeURIComponent(row.id)}`, {
        method: 'DELETE',
        headers: await getAuthHeaders(),
      });
      if (!response.ok) throw new Error(await errorMessage(response, t('store.skuCrosswalk.deleteError')));
    },
    onSuccess: () => {
      invalidate();
      showToast(t('store.skuCrosswalk.legacyDeleted'), 'success');
      setLegacyToDelete(null);
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const migrateMutation = useMutation({
    mutationFn: async () => {
      const response = await makeApiCall('/store/sku-crosswalk/migrate', {
        method: 'POST',
        headers: await getAuthHeaders(),
      });
      if (!response.ok) throw new Error(await errorMessage(response, t('store.skuCrosswalk.migrateError')));
      return response.json() as Promise<{ scanned: number; copied: number; alreadyMigrated: number; skipped: number }>;
    },
    onSuccess: (result) => {
      invalidate();
      showToast(t('store.skuCrosswalk.migrated', { copied: result.copied, already: result.alreadyMigrated, skipped: result.skipped }), 'success');
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const uploadCsv = async (path: 'preview' | 'import', file: File) => {
    const headers = (await getAuthHeaders()) as Record<string, string>;
    delete headers['Content-Type'];
    const body = new FormData();
    body.append('file', file);
    const response = await makeApiCall(`/store/sku-crosswalk/${path}`, { method: 'POST', headers, body });
    // A non-JSON failure on import (a gateway timeout) is not a problem with the file.
    const fallback = path === 'import' ? t('store.skuCrosswalk.importFailed') : t('store.skuCrosswalk.importError');
    if (!response.ok) throw new Error(await errorMessage(response, fallback));
    return response.json();
  };

  const previewMutation = useMutation({
    mutationFn: (file: File) => uploadCsv('preview', file) as Promise<CsvPreview>,
    onSuccess: (preview) => {
      setImportPreview(preview);
      setImportResult(null);
      setImportOpen(true);
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const importMutation = useMutation({
    mutationFn: (file: File) => uploadCsv('import', file) as Promise<CsvResult>,
    onSuccess: (result) => {
      setImportResult(result);
      setImportPreview(null);
      invalidate();
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const closeImport = () => {
    setImportOpen(false);
    setImportFile(null);
    setImportPreview(null);
    setImportResult(null);
  };

  const templateProps = {
    fileName: SKU_CROSSWALK_TEMPLATE.fileName,
    columns: templateColumns(
      SKU_CROSSWALK_TEMPLATE.headers,
      SKU_CROSSWALK_TEMPLATE.rows,
      Object.fromEntries(
        SKU_CROSSWALK_TEMPLATE.headers.map((name) => [
          name,
          { required: CROSSWALK_REQUIRED[name], description: t(`store.skuCrosswalk.template.${name}`) },
        ]),
      ),
    ),
    exampleRows: SKU_CROSSWALK_TEMPLATE.rows.map((row) => [...row]),
  };

  const handleExport = async () => {
    try {
      const response = await makeApiCall('/store/sku-crosswalk/export', { headers: await getAuthHeaders() });
      if (!response.ok) throw new Error(await errorMessage(response, t('store.skuCrosswalk.exportError')));
      const url = URL.createObjectURL(await response.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sku-crosswalk.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showToast((err as Error).message, 'error');
    }
  };

  const openAdd = () => {
    setForm(emptyForm());
    setProductQuery('');
    setFormOpen(true);
  };

  const openAddFor = (request: MapManuallyRequest) => {
    setForm({ ...emptyForm(), storeSku: request.storeSku, storeBarcode: request.storeBarcode, integrationId: request.integrationId });
    setProductQuery('');
    setFormOpen(true);
  };

  const openEdit = (m: CrosswalkMapping) => {
    setForm({
      id: m.id,
      product: m.product || (m.productId ? { id: m.productId, name: m.productId } : null),
      storeSku: m.storeSku || '',
      storeBarcode: m.storeBarcode || '',
      condition: m.condition,
      integrationId: m.integrationId || '',
      label: m.label || '',
    });
    setProductQuery('');
    setFormOpen(true);
  };

  const mappings = data?.mappings || [];
  const total = data?.total || 0;
  const resolutionCounts = data?.resolutionCounts;
  // The edit dialog keeps the row's own link; a staged or unresolved row has no catalog product to pick.
  const canSave = (Boolean(form.product) || Boolean(form.id)) && Boolean(form.storeSku.trim() || form.storeBarcode.trim());

  const columns = COLUMNS.map((c) => ({ ...c, header: t(`store.skuCrosswalk.columns.${c.header}`) }));

  const resolutionKindOf = (m: CrosswalkMapping): CrosswalkResolutionKind => m.resolution?.kind ?? (m.productId ? 'catalog' : 'unresolved');
  const resolutionChip = (m: CrosswalkMapping): React.ReactNode => {
    if (!queueWindowEnabled) return null;
    const kind = resolutionKindOf(m);
    const state = kind === 'staged' && m.resolution?.stagedProductId ? queueStates?.get(m.resolution.stagedProductId) : undefined;
    const label = kind === 'staged' && state
      ? `${t('store.skuCrosswalk.resolution.staged')} · ${t(`store.skuCrosswalk.queue.state.${state}`)}`
      : t(`store.skuCrosswalk.resolution.${kind}`);
    const tone = kind === 'staged' && state ? QUEUE_STATE_TONE[state] : RESOLUTION_TONE[kind];
    return <StatusChip size="small" tone={tone} label={label} />;
  };

  const renderCell = (m: CrosswalkMapping, key: string): React.ReactNode => {
    const index = mappings.findIndex((row) => row.id === m.id);
    const continuation = index > 0 && Boolean(m.productId) && mappings[index - 1].productId === m.productId;
    switch (key) {
      case 'product':
        return (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={continuation ? 400 : 600} noWrap color={continuation ? 'text.secondary' : 'text.primary'}>
              {m.product?.name || m.productId || m.label || t(`store.skuCrosswalk.resolution.${resolutionKindOf(m)}`)}
            </Typography>
            <Typography variant="caption" sx={{ color: tokens.ink.faint }} noWrap component="div">
              {[m.product?.brand, m.product?.sku, m.label].filter(Boolean).join(' · ')}
            </Typography>
            {resolutionChip(m) && <Box sx={{ mt: 0.5 }}>{resolutionChip(m)}</Box>}
          </Box>
        );
      case 'sku':
        return (
          <Stack spacing={0.5} alignItems="flex-start">
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{m.storeSku || '—'}</Typography>
            {m.source === 'merge_approval' && (
              <StatusChip size="small" tone="info" label={t('store.skuCrosswalk.sourceMergeApproval')} />
            )}
          </Stack>
        );
      case 'barcode':
        return <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{m.storeBarcode || '—'}</Typography>;
      case 'condition':
        return <StatusChip label={t(`store.skuCrosswalk.condition.${m.condition}`)} tone={CONDITION_TONE[m.condition]} />;
      case 'integration':
        return <Typography variant="body2">{integrationName(m.integrationId)}</Typography>;
      default:
        return (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            <Tooltip title={t('common.edit')}>
              <IconButton size="small" aria-label={t('common.edit')} onClick={() => openEdit(m)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('common.delete')}>
              <IconButton size="small" aria-label={t('common.delete')} onClick={() => setToDelete(m)}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
    }
  };

  return (
    <PageContainer maxWidth={1200}>
      <PageHeader
        title={t('store.skuCrosswalk.title')}
        description={t('store.skuCrosswalk.description')}
        actions={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <input
              type="file"
              accept=".csv,.xlsx"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (fileInputRef.current) fileInputRef.current.value = '';
                if (!file) return;
                setImportFile(file);
                previewMutation.mutate(file);
              }}
            />
            <Button variant="outlined" startIcon={<CloudUpload />} onClick={() => fileInputRef.current?.click()} disabled={previewMutation.isPending}>
              {t('store.skuCrosswalk.importCsv')}
            </Button>
            <Tooltip title={total === 0 ? t('store.skuCrosswalk.exportDisabled') : ''}>
              <span>
                <Button variant="outlined" startIcon={<Download />} onClick={handleExport} disabled={total === 0}>
                  {t('common.export')}
                </Button>
              </span>
            </Tooltip>
            <CsvTemplateHint
              {...templateProps}
              compact
              size="medium"
              extraActions={<ProductsCsvExportButton path="/store/sku-crosswalk/products.csv" fileName="sku-crosswalk-products.csv" size="medium" />}
            />
            {legacyRows.length > 0 && (
              <Tooltip title={t('store.skuCrosswalk.copyLegacyHelp')}>
                <span>
                  <Button variant="outlined" startIcon={migrateMutation.isPending ? <CircularProgress size={16} /> : <SyncAlt />} onClick={() => migrateMutation.mutate()} disabled={migrateMutation.isPending}>
                    {t('store.skuCrosswalk.copyLegacy')}
                  </Button>
                </span>
              </Tooltip>
            )}
            <Button variant="contained" startIcon={<Add />} onClick={openAdd}>
              {t('store.skuCrosswalk.add')}
            </Button>
          </Stack>
        }
      />

      {error && <InlineMessage severity="error">{(error as Error).message}</InlineMessage>}

      {tabsShown && (
        <TabShell
          sticky={false}
          value={activeTab}
          onChange={setTab}
          aria-label={t('store.skuCrosswalk.tabsAria')}
          tabs={[
            { key: 'crosswalk' as const, label: t('store.skuCrosswalk.tabs.crosswalk') },
            ...(conditionTabsVisible
              ? [
                  { key: 'pricing' as const, label: t('store.skuCrosswalk.tabs.pricing') },
                  { key: 'units' as const, label: t('store.skuCrosswalk.tabs.units') },
                ]
              : []),
            ...(queueWindowEnabled
              ? [{ key: 'queue' as const, label: t('store.skuCrosswalk.tabs.queue', { count: resolutionCounts?.staged ?? 0 }) }]
              : []),
          ]}
        >
          {activeTab === 'queue' && <CatalogQueueCard />}
          {activeTab === 'pricing' && (conditionSurface.mode === 'locked' ? (
            <LockedFeature variant="inline" planName={conditionSurface.planName} title={t('store.skuCrosswalk.tabs.pricing')} />
          ) : conditionSurface.hold ? (
            <LoadingState />
          ) : (
            <ConditionPricingCard />
          ))}
          {activeTab === 'units' && (conditionSurface.mode === 'locked' ? (
            <LockedFeature variant="inline" planName={conditionSurface.planName} title={t('store.skuCrosswalk.tabs.units')} />
          ) : conditionSurface.hold ? (
            <LoadingState />
          ) : (
            <StoreUnitsTab />
          ))}
        </TabShell>
      )}

      {activeTab === 'crosswalk' && (<>
      <CsvTemplateHint {...templateProps} explainerOnly intro={t('store.skuCrosswalk.template.intro')} />

      <UnmappedReviewCard initialSource={reviewSource} onMapManually={openAddFor} />

      <SectionCard
        dense
        title={t('store.skuCrosswalk.listTitle', { count: total })}
        actions={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {queueWindowEnabled && resolutionCounts && (
              <TextField
                size="small"
                select
                value={resolutionFilter}
                onChange={(e) => {
                  setResolutionFilter(e.target.value as '' | CrosswalkResolutionKind);
                  setPage(0);
                }}
                SelectProps={{ displayEmpty: true }}
                sx={{ minWidth: 170 }}
                aria-label={t('store.skuCrosswalk.resolutionFilterAria')}
              >
                <MenuItem value="">{t('store.skuCrosswalk.allResolutions', { count: RESOLUTION_KINDS.reduce((n, k) => n + (resolutionCounts[k] || 0), 0) })}</MenuItem>
                {RESOLUTION_KINDS.map((k) => (
                  <MenuItem key={k} value={k}>{`${t(`store.skuCrosswalk.resolution.${k}`)} (${resolutionCounts[k] || 0})`}</MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              size="small"
              select
              value={conditionFilter}
              onChange={(e) => {
                setConditionFilter(e.target.value as '' | CrosswalkCondition);
                setPage(0);
              }}
              SelectProps={{ displayEmpty: true }}
              sx={{ minWidth: 140 }}
              aria-label={t('store.skuCrosswalk.columns.condition')}
            >
              <MenuItem value="">{t('store.skuCrosswalk.allConditions')}</MenuItem>
              {CONDITIONS.map((c) => (
                <MenuItem key={c} value={c}>{t(`store.skuCrosswalk.condition.${c}`)}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              placeholder={t('store.skuCrosswalk.searchPlaceholder')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
              sx={{ minWidth: { xs: 160, sm: 260 } }}
            />
          </Stack>
        }
      >
        <ResponsiveRows
          columns={columns}
          headerCellSx={{ textTransform: 'none', letterSpacing: 0, fontSize: 13, fontWeight: 600, color: tokens.ink.body }}
          rows={mappings}
          getRowKey={(m) => m.id}
          renderCell={renderCell}
          isLoading={isLoading}
          emptyState={
            <EmptyState
              size="compact"
              title={search || conditionFilter || resolutionFilter ? t('store.skuCrosswalk.noMatches') : t('store.skuCrosswalk.emptyTitle')}
              description={search || conditionFilter || resolutionFilter ? undefined : t('store.skuCrosswalk.emptyDescription')}
              actionLabel={search || conditionFilter || resolutionFilter ? undefined : t('store.skuCrosswalk.add')}
              onAction={search || conditionFilter || resolutionFilter ? undefined : openAdd}
            />
          }
        />
        {total > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[25, 50, 100]}
            onPageChange={(_e, next) => setPage(next)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        )}
      </SectionCard>

      {legacyRows.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <SectionCard
            dense
            title={t('store.skuCrosswalk.legacyTitle', { count: legacyRows.length })}
            actions={
              <Button size="small" onClick={() => migrateMutation.mutate()} disabled={migrateMutation.isPending}>
                {t('store.skuCrosswalk.copyLegacy')}
              </Button>
            }
          >
            <Box sx={{ px: '20px', py: 1.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('store.skuCrosswalk.legacyHelp')}
              </Typography>
              {legacyRows.slice(0, 50).map((row) => (
                <Stack key={row.id} direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2">
                    {[row.storeSku, row.storeBarcode, row.productName, row.upc || row.matchSku].filter(Boolean).join(' · ')}
                  </Typography>
                  <Tooltip title={t('common.delete')}>
                    <IconButton size="small" aria-label={t('common.delete')} onClick={() => setLegacyToDelete(row)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
              {legacyRows.length > 50 && (
                <Typography variant="caption" color="text.secondary">
                  {t('store.skuCrosswalk.legacyMore', { count: legacyRows.length - 50 })}
                </Typography>
              )}
            </Box>
          </SectionCard>
        </Box>
      )}
      </>)}

      <FormDialog
        open={formOpen}
        title={t(form.id ? 'store.skuCrosswalk.editTitle' : 'store.skuCrosswalk.addTitle')}
        onClose={() => !saveMutation.isPending && setFormOpen(false)}
        onSubmit={() => saveMutation.mutate(form)}
        submitLabel={t('common.save')}
        submitDisabled={!canSave}
        loading={saveMutation.isPending}
        maxWidth="sm"
        disableEnforceFocus
      >
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Autocomplete
            fullWidth
            options={productOptions}
            value={form.product}
            inputValue={productQuery}
            loading={productsLoading}
            disabled={Boolean(form.id)}
            filterOptions={(options) => options}
            getOptionLabel={productLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={productQuery.trim().length < 2 ? t('store.skuCrosswalk.productHint') : t('store.skuCrosswalk.noProducts')}
            onChange={(_e, value) => setForm((f) => ({ ...f, product: value }))}
            onInputChange={(_e, value, reason) => {
              if (reason === 'reset') return;
              setProductQuery(value);
            }}
            slotProps={{ popper: { sx: { zIndex: (theme) => theme.zIndex.modal + 1 } } }}
            renderInput={(params) => (
              <TextField {...params} label={t('store.skuCrosswalk.fields.product')} helperText={t('store.skuCrosswalk.fields.productHelp')} />
            )}
          />
          {placeholdersEnabled && !form.id && !form.product && (
            <Box>
              {!placeholderOpen ? (
                <Button size="small" onClick={() => setPlaceholderOpen(true)} disabled={!form.storeSku.trim() && !form.storeBarcode.trim()}>
                  {t('store.placeholders.crosswalkCreate')}
                </Button>
              ) : (
                <Stack spacing={1.5}>
                  <Typography variant="caption" color="text.secondary">{t('store.placeholders.crosswalkHelp')}</Typography>
                  <BrandSelect label={t('store.placeholders.crosswalkBrand')} value={placeholderBrand} onChange={setPlaceholderBrand} />
                  <TextField size="small" label={t('store.placeholders.crosswalkTitle')} value={placeholderTitle} onChange={(e) => setPlaceholderTitle(e.target.value)} fullWidth />
                  <Box>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={placeholderMutation.isPending || !placeholderBrand.trim()}
                      onClick={() => placeholderMutation.mutate(form)}
                    >
                      {t('store.placeholders.createFromListing')}
                    </Button>
                  </Box>
                </Stack>
              )}
            </Box>
          )}
          <TextField
            label={t('store.skuCrosswalk.fields.storeSku')}
            value={form.storeSku}
            onChange={(e) => setForm((f) => ({ ...f, storeSku: e.target.value }))}
            helperText={t('store.skuCrosswalk.fields.storeSkuHelp')}
            fullWidth
          />
          <TextField
            label={t('store.skuCrosswalk.fields.storeBarcode')}
            value={form.storeBarcode}
            onChange={(e) => setForm((f) => ({ ...f, storeBarcode: e.target.value }))}
            helperText={t('store.skuCrosswalk.fields.storeBarcodeHelp')}
            fullWidth
          />
          <TextField
            select
            label={t('store.skuCrosswalk.fields.condition')}
            value={form.condition}
            onChange={(e) => setForm((f) => ({ ...f, condition: e.target.value as CrosswalkCondition }))}
            helperText={t('store.skuCrosswalk.fields.conditionHelp')}
            fullWidth
          >
            {CONDITIONS.map((c) => (
              <MenuItem key={c} value={c}>{t(`store.skuCrosswalk.condition.${c}`)}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('store.skuCrosswalk.fields.integration')}
            value={form.integrationId}
            onChange={(e) => setForm((f) => ({ ...f, integrationId: e.target.value }))}
            helperText={t('store.skuCrosswalk.fields.integrationHelp')}
            SelectProps={{ displayEmpty: true }}
            fullWidth
          >
            <MenuItem value="">{t('store.skuCrosswalk.allIntegrations')}</MenuItem>
            {(integrations || []).map((i) => (
              <MenuItem key={i.id} value={i.id}>{integrationName(i.id)}</MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('store.skuCrosswalk.fields.label')}
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            fullWidth
          />
        </Stack>
      </FormDialog>

      <ConfirmDialog
        open={Boolean(toDelete)}
        title={t('store.skuCrosswalk.deleteTitle')}
        message={t('store.skuCrosswalk.deleteMessage', { value: toDelete?.storeSku || toDelete?.storeBarcode || '' })}
        confirmLabel={t('common.delete')}
        confirmColor="error"
        loading={deleteMutation.isPending}
        onConfirm={() => toDelete && deleteMutation.mutate(toDelete)}
        onClose={() => setToDelete(null)}
      />

      <ConfirmDialog
        open={Boolean(legacyToDelete)}
        title={t('store.skuCrosswalk.legacyDeleteTitle')}
        message={t('store.skuCrosswalk.legacyDeleteMessage', { value: legacyToDelete?.storeSku || legacyToDelete?.storeBarcode || '' })}
        confirmLabel={t('common.delete')}
        confirmColor="error"
        loading={deleteLegacyMutation.isPending}
        onConfirm={() => legacyToDelete && deleteLegacyMutation.mutate(legacyToDelete)}
        onClose={() => setLegacyToDelete(null)}
      />

      <FormDialog
        open={importOpen}
        title={t('store.skuCrosswalk.importTitle')}
        onClose={closeImport}
        onSubmit={() => (importResult ? closeImport() : importFile && importMutation.mutate(importFile))}
        submitLabel={importResult ? t('common.close') : t('store.skuCrosswalk.importRows', { count: importPreview?.totalRows ?? 0 })}
        submitDisabled={!importResult && (!importPreview || importPreview.totalRows === 0)}
        loading={importMutation.isPending}
        hideCancel={Boolean(importResult)}
      >
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          {importFile && <Typography variant="body2" color="text.secondary">{importFile.name}</Typography>}
          {importPreview && (
            <>
              <Typography variant="body2">{t('store.skuCrosswalk.previewRows', { count: importPreview.totalRows })}</Typography>
              {(importPreview.blank ?? 0) > 0 && (
                <Typography variant="body2" color="text.secondary">{t('store.skuCrosswalk.blankRows', { count: importPreview.blank })}</Typography>
              )}
              {importPreview.errors.length > 0 && (
                <InlineMessage severity="warning">
                  {importPreview.errors.slice(0, 10).map((e) => `${t('store.skuCrosswalk.row')} ${e.row}: ${e.error}`).join(' | ')}
                </InlineMessage>
              )}
              {(importPreview.warnings?.length ?? 0) > 0 && (
                <InlineMessage severity="info">
                  {(importPreview.warnings ?? []).slice(0, 10).map((w) => `${t('store.skuCrosswalk.row')} ${w.row}: ${w.warning}`).join(' | ')}
                </InlineMessage>
              )}
              {importPreview.resolutions && importPreview.resolutions.length > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{t('store.skuCrosswalk.previewResolutions')}</Typography>
                  <Stack spacing={0.5}>
                    {importPreview.resolutions.map((r) => {
                      const row = importPreview.preview?.find((p) => p.rowNumber === r.row);
                      return (
                        <Stack key={r.row} direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                          <Typography variant="caption" sx={{ color: tokens.ink.faint, minWidth: 56 }}>{`${t('store.skuCrosswalk.row')} ${r.row}`}</Typography>
                          <Typography variant="body2" noWrap sx={{ minWidth: 0, flex: 1 }}>
                            {[row?.storeSku || row?.storeBarcode, r.name || row?.catalogSku || row?.upc || row?.productId].filter(Boolean).join(' → ')}
                          </Typography>
                          <StatusChip size="small" tone={PREVIEW_TONE[r.resolution]} label={t(`store.skuCrosswalk.previewResolution.${r.resolution}`)} />
                        </Stack>
                      );
                    })}
                  </Stack>
                  {importPreview.resolutions.some((r) => r.resolution === 'ambiguous') && (
                    <InlineMessage severity="warning">
                      {importPreview.resolutions.filter((r) => r.resolution === 'ambiguous').slice(0, 5).map((r) => `${t('store.skuCrosswalk.row')} ${r.row}: ${r.message}`).join(' | ')}
                    </InlineMessage>
                  )}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary">{t('store.skuCrosswalk.csvHelp')}</Typography>
            </>
          )}
          {importResult && (
            <>
              <Typography variant="body2">
                {t('store.skuCrosswalk.importResult', { created: importResult.created, updated: importResult.updated, skipped: importResult.skipped })}
              </Typography>
              {importResult.resolution && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {RESOLUTION_KINDS.map((k) => (
                    <StatusChip key={k} size="small" tone={RESOLUTION_TONE[k]} label={`${t(`store.skuCrosswalk.resolution.${k}`)} · ${importResult.resolution?.[k] ?? 0}`} />
                  ))}
                </Stack>
              )}
              {(importResult.blank ?? 0) > 0 && (
                <Typography variant="body2" color="text.secondary">{t('store.skuCrosswalk.blankRows', { count: importResult.blank })}</Typography>
              )}
              {importResult.errors.length > 0 && (
                <InlineMessage severity="warning">
                  {importResult.errors.slice(0, 10).map((e) => `${t('store.skuCrosswalk.row')} ${e.row}: ${e.error}`).join(' | ')}
                </InlineMessage>
              )}
              {(importResult.warnings?.length ?? 0) > 0 && (
                <InlineMessage severity="info">
                  {(importResult.warnings ?? []).slice(0, 10).map((w) => `${t('store.skuCrosswalk.row')} ${w.row}: ${w.warning}`).join(' | ')}
                </InlineMessage>
              )}
            </>
          )}
        </Stack>
      </FormDialog>
    </PageContainer>
  );
};

export default SkuCrosswalkPanel;
